import React from 'react'
import { Trophy, Brain, Swords, Flag, BarChart3 } from 'lucide-react'
import { AppMark } from '@unity-cosmos/chrome'
import { LangToggle } from '../games/lib/LangToggle.jsx'
import { tr, useLang } from '../games/lib/lang.js'

export function TopNav({
  score = 0,
  rank = null,
  activeTab = 'train',
  onTabChange,
}) {
  const [lang, setLang] = useLang()
  const navItems = [
    { id: 'train', label: tr(lang, 'Entrenar', 'Train'), icon: Brain },
    { id: 'duels', label: tr(lang, 'Duelos 1v1', 'Duels 1v1'), icon: Swords },
    { id: 'missions', label: tr(lang, 'Misiones', 'Missions'), icon: Flag },
    { id: 'ranking', label: tr(lang, 'Ranking', 'Ranking'), icon: BarChart3 },
  ]

  return (
    <header className="sticky top-0 z-30 bg-[#0a0d14]/90 backdrop-blur-md border-b border-slate-800/60 w-full">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between gap-4">
        {/* Brand / Logo */}
        <div className="flex items-center gap-3">
          <AppMark id="brainpower" size={36} className="shrink-0" />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-black text-white tracking-wide">
                COSMOS BRAIN POWER
              </span>
              <span className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-cyan-950 border border-cyan-500/40 text-cyan-300 uppercase">
                {tr(lang, 'Detox de dopamina', 'Dopamine Detox')}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 hidden sm:block">
              {tr(lang, 'Entrenamiento cognitivo en la constelación Unity Cosmos', 'Cognitive training in the Unity Cosmos constellation')}
            </p>
          </div>
        </div>

        {/* Desktop Navigation Tabs */}
        <nav className="hidden md:flex items-center gap-1 bg-slate-900/80 p-1 rounded-xl border border-slate-800">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = activeTab === item.id
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onTabChange?.(item.id)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  isActive
                    ? 'bg-amber-400 text-slate-950 shadow-md shadow-amber-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`}
              >
                <Icon size={14} />
                <span>{item.label}</span>
              </button>
            )
          })}
        </nav>

        {/* Score, global rank, profile */}
        <div className="flex items-center gap-2 sm:gap-3">
          <LangToggle lang={lang} onChange={setLang} />
          <button
            type="button"
            onClick={() => onTabChange?.('ranking')}
            className="flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full bg-slate-900/90 border border-cyan-500/30 text-cyan-300 text-xs font-bold shadow-[0_0_10px_rgba(6,182,212,0.15)] cursor-pointer"
            aria-label={tr(lang, `Puntuación ${score}`, `Score ${score}`)}
          >
            <Trophy size={13} className="text-cyan-300" />
            <span>{Number(score || 0).toLocaleString(lang === 'en' ? 'en-US' : 'es-MX')}</span>
            <span className="hidden sm:inline text-[10px] text-cyan-300/70 font-medium">pts</span>
          </button>

          <button
            type="button"
            onClick={() => onTabChange?.('ranking')}
            className="flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full bg-slate-900/90 border border-amber-500/30 text-amber-300 text-xs font-bold shadow-[0_0_10px_rgba(245,158,11,0.15)] cursor-pointer"
            aria-label={rank
              ? tr(lang, `Ranking global número ${rank}`, `Global rank number ${rank}`)
              : tr(lang, 'Sin puesto en el ranking global', 'No global rank yet')}
          >
            <span className="text-[10px] font-medium text-amber-200/80">{tr(lang, 'Global', 'Global')}</span>
            <span>{rank ? `#${rank}` : '—'}</span>
          </button>
        </div>
      </div>
    </header>
  )
}
